import SubPageLayout from '@/app/features/organisation/SubPageLayout';
import { useReportContext } from '@/app/features/reports/contexts/report-context';
import { useTestEditor } from '@/app/features/reports/hooks/useTestEditor';
import VirtualizedNestedList from '@/app/features/reports/VirtualisedItemList';
import PrintableReport from './PrintableReport';
import { useAppLayoutContext } from '@/app/layout2/AppLayout';
import { withLazyLoad } from '@/app/utils/withLazyLoad';
import EditModeToggle from '@/components/raytd/edit-mode-toggle';
import PresenceIndicator from '@/components/raytd/presence-indicator';
import { useReportPresence } from '@/hooks/use-presence';
import { TestStatus } from '@app.raytd.com/common/types/test-form';
import { useAddCustomFieldOptionMutation, useExportReportRevisionWordMutation, isAPIError, rAPI } from '@app.raytd.com/store';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { v4 } from 'uuid';
import ReportToolbar from './components/report-editing-toolbar';
import ReportFilterBar from './components/report-filter-bar';
import { ReportViewerContext, useReportViewContext } from './components/report-viewer-context';
import SdaResultsColumnHeadings from './components/sda-results-column-headings';
import StickyHeader from './components/sticky-header';
import { ToggleProvider } from './components/test-tile/toggle-context';
import { ReportViewModes, ScrollPosition } from './types';
import { useReportGroupingContext } from './contexts/grouping-context';
import { type ExportScope } from '@/app/features/reports/published/components/report-export-dialog';
import { pollForExportCompletion } from '@/app/features/reports/utils/poll-export-status';


type ReportViewerProps = {
    reportId: string;
    initialEditMode?: boolean;
    readOnly?: boolean;
}

const ReportViewer: React.FC<ReportViewerProps> = ({ reportId, readOnly = false }) => {

    const [viewMode, setViewMode] = useState<ReportViewModes>('details');
    const { isPublished, permissions, revisionId, revision } = useReportContext();
    const [showMissingData, setShowMissingData] = useState(false);
    const [showFastFillQA, setShowFastFillQA] = useState(false);
    const [showStatus, setShowStatus] = useState(false);

    const LazyTestEditDialog = useMemo(
        () => withLazyLoad(
            () => import('./components/test-edit-dialog'),
            { condition: !readOnly }
        ),
        [readOnly]
    );

    const [isScrolling, setIsScrolling] = useState(false);
    const { mainPageElement } = useAppLayoutContext();

    const memoizedScrollParent = useMemo(() => mainPageElement, [mainPageElement]);

    const {
        data,
        filteredData,
        testSuites,
        report,
        filters,
        missingDataCount,
        missingSegments,
        bookmarks,
        isEditMode: editMode,
        setEditMode,
        viewId,
        isSdaFormat,
    } = useReportViewContext();
    
    const isPrintMode = viewId === 'print';
    const { expandAll, expandAllTiles } = useReportGroupingContext();
    
    // Expand all headers and tiles when in print mode and data is loaded
    useEffect(() => {
        if (isPrintMode && data && data.length > 0) {
            expandAll();
            // Small delay to ensure headers are expanded before expanding tiles
            setTimeout(() => {
                expandAllTiles();
            }, 100);
        }
    }, [isPrintMode, data, expandAll, expandAllTiles]);


    // const { data: testSuites, isLoading: isLoadingSpecifications } = useGetReportTestSuitesQuery(report?.id ? Number(report?.id) : skipToken);
    const inspections = report?.assessments || []
    const assets = report?.assets || []

    const [exportWord, { isLoading: isExportingWord }] = useExportReportRevisionWordMutation()

    const {
        isOpen,
        testItem,
        isLoading,
        openEditor,
        closeEditor
    } = useTestEditor({
        onEditorClose: () => {
            //toast.success('Test closed');
        }
    });

    const [scrollPosition, setScrollPosition] = useState<ScrollPosition>({
        range: {
            startIndex: 0,
            endIndex: 0
        },
        relativePositions: {
            startIndex: 0,
            endIndex: 0
        }
    });

    const handleScrollRangeChange = useCallback((position) => {
        setScrollPosition(position);
    }, [data]);

    const listRef = useRef<any>(null);

    const handleBookmarkClick = useCallback((bookmark) => {
        console.debug('scroll to', bookmark, listRef)
        listRef.current?.scrollToIndex(bookmark.index, 'start');
    }, []);

    const scrollToTest = useCallback((id: number) => {
        listRef.current?.scrollToTest(id);
    }, []);

    const updateTestStatus = useCallback(async (id: string, status: TestStatus) => {

        toast.success(`Updating test ${id} to status ${status}`);
    }, []);

    const handleFilterChange = useCallback((value: string) => {
        filters?.setFilterInput(value);
    }, [filters?.setFilterInput]);

    const [isUpdatingTestId, setUpdatingTestId] = useState<number | null>(null);
    const [bulkUpdatingTestIds, setBulkUpdatingTestIds] = useState<Set<number>>(new Set());

    const setUpdatingTest = useCallback((id: number) => {
        setUpdatingTestId(id);
    }, []);

    const clearUpdatingTest = useCallback(() => {
        setUpdatingTestId(null);
    }, []);

    const { updateStatus } = useReportPresence(reportId);

    useEffect(() => {
        updateStatus('viewing');

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                updateStatus('viewing');
            }
        };

        window.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('visibilitychange', handleVisibilityChange);
            updateStatus('left');
        };
    }, [reportId, updateStatus]);

    const handleEditStart = () => {
        updateStatus('editing');
    };

    const [addCustomFieldOption] = useAddCustomFieldOptionMutation();

    const handleCustomFieldOptionAdded = useCallback(async (testSuiteId: number, fieldId: string, value: string) => {
        try {
            const result = await addCustomFieldOption({
                testSuiteId: testSuiteId,
                reportId: report?.id ? Number(report?.id) : null,
                field_id: fieldId,
                id: v4(),
                displayName: value,
            }).unwrap();

            return { success: true, field_id: result.field_id, data: result.data };
        } catch (error) {
            console.error('handleCustomFieldOptionAdded error', error);
            throw error;
        }
    }, [addCustomFieldOption, report?.id]);

    const pollingCleanupRef = useRef<(() => void) | null>(null)

    useEffect(() => {
        return () => {
            if (pollingCleanupRef.current) {
                pollingCleanupRef.current()
                pollingCleanupRef.current = null
            }
        }
    }, [])

    const handleDialogExportWord = useCallback(async ({ scope, groupBy }: { scope: ExportScope; groupBy: 'location' | 'test' | 'element_group' }) => {
        console.debug('Exporting Word from editable view with scope:', scope, 'groupBy:', groupBy)

        const effectiveRevisionId = revisionId ?? revision?.id;

        if (!effectiveRevisionId) {
            // No revision id available to export – log for debugging and exit early
            console.warn('No revision found to export from editable view', {
                revisionId,
                revision,
            });
            return;
        }

        if (pollingCleanupRef.current) {
            pollingCleanupRef.current()
            pollingCleanupRef.current = null
        }

        try {
            const result = await exportWord({
                revisionId: Number(effectiveRevisionId),
                groupBy,
                // Use live source to export current report data for this revision
                source: 'live' as 'live',
                report_id: report?.id ? Number(report.id) : undefined,
            }).unwrap()

            if (result.export_id) {
                console.debug(`Editable export ${result.export_id} started. Polling...`)

                pollingCleanupRef.current = pollForExportCompletion(result.export_id, {
                    onCompleted: () => {
                        pollingCleanupRef.current = null
                        toast.success("Export completed", {
                            description: "Your Word export for the current revision is ready. You will receive a notification with the download link.",
                        })
                    },
                    onFailed: (error) => {
                        pollingCleanupRef.current = null
                        toast.error("Export Failed", {
                            description: error.message || "Export generation failed on server.",
                        })
                    },
                    onTimeout: () => {
                        pollingCleanupRef.current = null
                        toast.info("Export in Progress", {
                            description: "Export is still in progress. Please check your notifications later.",
                        })
                    },
                    maxDuration: 5 * 60 * 1000,
                    interval: 3000,
                })

                toast.success("Generating export...", {
                    description: "Your Word export for the current revision is being generated.",
                })
            } else {
                toast.success("Generating export...", {
                    description: "Your Word export for the current revision is being generated. You will receive a notification when it is ready to download.",
                })
            }
        } catch (error) {
            if (isAPIError(error)) {
                if (error.status === 401) {
                    toast.error("Please log in to export reports", {
                        description: "You need to be logged in to export reports.",
                    })
                    return
                }

                if (error.status === 403) {
                    toast.error("Permission Denied", {
                        description: "You don't have permission to export this report.",
                    })
                    return
                }

                if (error.status === 422) {
                    const errorCode = (error.data as { code?: string } | undefined)?.code

                    if (errorCode === 'EMPTY_REPORT') {
                        toast.error("Export Failed", {
                            description: "This report has no test items to export.",
                        })
                        return
                    }

                    if (errorCode === 'INVALID_GROUPING') {
                        toast.error("Export Failed", {
                            description: "The selected grouping is not valid for this report. Please choose a different grouping and try again.",
                        })
                        return
                    }

                    if (errorCode === 'INVALID_SOURCE') {
                        toast.error("Export Failed", {
                            description: "The export source is invalid. Please refresh the page and try again or contact support.",
                        })
                        return
                    }

                    if (errorCode === 'LIVE_REPORT_MISMATCH') {
                        toast.error("Export Failed", {
                            description: "The selected report does not match the revision being exported. Please refresh the page and try again or contact support.",
                        })
                        return
                    }

                    if (errorCode === 'INVALID_REPORT') {
                        toast.error("Export Failed", {
                            description: "The report selected for live export is invalid. Please refresh the page and try again or contact support.",
                        })
                        return
                    }

                    if (errorCode === 'PUBLISHED_SNAPSHOT_NOT_FOUND') {
                        toast.error("Export Failed", {
                            description: "A published snapshot could not be found for this revision. Please ensure the report has a valid published state.",
                        })
                        return
                    }

                    toast.error("Export Failed", {
                        description: "This report cannot be exported due to a validation error. Please try again or contact support.",
                    })
                    return
                }

                if (error.status === 500) {
                    toast.error("Export Failed", {
                        description: "The export failed on the server. Please try again or contact support.",
                    })
                    return
                }
            }

            toast.error("Error", {
                description: "An error occurred. Please try again or contact support.",
            })
        }
    }, [revisionId, exportWord])

    return (
        (<SubPageLayout
            title={isPrintMode && !isSdaFormat ? '' : 'Results'}
            rightComponents={
                !isPrintMode ? (
                    <>
                        <PresenceIndicator reportId={reportId} />
                        {!readOnly && <EditModeToggle editMode={editMode} setEditMode={setEditMode} />}
                    </>
                ) : undefined
            }
            className={isPrintMode ? 'bg-white' : ''}
            contentClassName={isPrintMode ? 'bg-white' : ''}
        >
            <ReportViewerContext.Provider
                value={{
                    reportId: report?.id ? Number(report?.id) : null,
                    activeItems: [], setActiveItems: () => { },
                    showTestEditor: openEditor,
                    setUpdatingTest: setUpdatingTest,
                    clearUpdatingTest: clearUpdatingTest,
                    isUpdatingTest: isUpdatingTestId !== null,
                    updatingTestId: isUpdatingTestId,
                    bulkUpdatingTestIds,
                    setBulkUpdatingTestIds,
                    scrollToTest: scrollToTest,
                    showMissingData,
                    setShowMissingData,
                    showStatus,
                    setShowStatus,
                    showFastFillQA,
                    setShowFastFillQA,
                    readOnly,
                }}>
                <ToggleProvider>
                    {!isPrintMode && (
                        <StickyHeader>
                            <ReportToolbar
                                setViewMode={setViewMode}
                                filters={filters}
                                onExportExcel={async () => Promise.resolve()}
                                onExportPdf={async () => Promise.resolve()}
                                onExportWord={handleDialogExportWord}
                                isExportingExcel={false}
                                isExportingPdf={false}
                                isExportingWord={isExportingWord}
                                canExportExcel={false}
                                canExportPdf={false}
                                canExportWord={true}
                                defaultScope={'all' as ExportScope}
                            />
                            <ReportFilterBar
                                handleFilterChange={handleFilterChange}
                                missingSegments={missingSegments}
                                missingDataCount={missingDataCount}
                                scrollToTest={scrollToTest}
                            />
                        </StickyHeader>
                    )}
                    <div
                        className={isPrintMode ? 'bg-white' : ''}
                        style={isPrintMode ? { backgroundColor: 'white' } : undefined}
                    >
                        <div className="print:hidden h-full">
                            {/* Static, non-sticky column-heading row for SDA — rendered once
                                above the results list, outside the virtualizer (raytd/web QA
                                108.03, item 1). */}
                            {isSdaFormat && !isPrintMode && <SdaResultsColumnHeadings />}
                            <VirtualizedNestedList
                                ref={listRef}
                                scrollParent={memoizedScrollParent}
                                setIsScrolling={setIsScrolling}
                                setScrollPosition={handleScrollRangeChange}
                            />
                        </div>
                        <div className="hidden print:block">
                            <PrintableReport />
                        </div>
                    </div>
                </ToggleProvider>
                <LazyTestEditDialog
                    isOpen={isOpen}
                    onClose={closeEditor}
                    testItem={testItem}
                    isLoading={isLoading}
                    onSubmit={(data) => {
                        console.debug('testEditDialog onSubmit', data);
                        setUpdatingTest(data.assessmentTestId);
                    }}
                    onCustomFieldOptionAdded={handleCustomFieldOptionAdded}
                    inspections={inspections}
                    assets={assets}
                    testSuites={testSuites as any}
                />
            </ReportViewerContext.Provider >

        </SubPageLayout >)
    );
};

const App: React.FC<ReportViewerProps> = ({ reportId, initialEditMode = true }) => {
    return (
        <ReportViewer
            reportId={reportId}
            readOnly={!initialEditMode}
            initialEditMode={initialEditMode}
        />
    );
}

export default App;